import { useState, useCallback } from 'react';
import { parseApiError, extractFieldErrors, getFriendlyErrorMessage } from '../utils/errorHandler';

function defaultFormatApiErrors(apiError, { fieldErrors, additionalMessages }) {
    const next = { ...fieldErrors };
    const submitParts = [];
    if (apiError.message?.trim()) {
        submitParts.push(apiError.message.trim());
    }
    for (const m of additionalMessages || []) {
        if (m?.trim()) {
            submitParts.push(m.trim());
        }
    }

    const hasFieldErrors = Object.keys(fieldErrors).length > 0;
    if (hasFieldErrors) {
        if (submitParts.length > 0) {
            next.submit = submitParts.join('\n\n');
        }
        return next;
    }
    if (submitParts.length > 0) {
        return { submit: submitParts.join('\n\n') };
    }
    return {
        submit: apiError.message?.trim() || getFriendlyErrorMessage(apiError),
    };
}

/**
 * Hook para gerenciamento de formulários
 * @param {Object} initialData - Dados iniciais do formulário
 * @param {Function} onSubmit - Função chamada ao submeter o formulário
 * @param {Function} validator - Função de validação customizada (opcional)
 * @returns {Object} Objeto com formData, errors, isSubmitting, handleChange, handleSubmit, resetForm, setFormData, setErrors
 */
/**
 * @param {Object} [apiErrorOptions] - Tratamento de erros 4xx/5xx da API no submit
 * @param {function(string): string|null} [apiErrorOptions.mapApiFieldLocation]
 * @param {function(Object, { fieldErrors: Object, additionalMessages: string[] }): Object} [apiErrorOptions.formatApiErrors]
 */
export function useForm(initialData = {}, onSubmit, validator = null, apiErrorOptions = null) {
    const [formData, setFormData] = useState(initialData);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    /**
     * Handler para mudanças em campos do formulário
     * @param {Event|Object} e - Event do input ou objeto { name, value }
     */
    const handleChange = useCallback((e) => {
        const name = e.target?.name || e.name;
        let value = e.target?.value !== undefined ? e.target.value : e.value;
        const type = e.target?.type || e.type;
        const checked = e.target?.checked !== undefined ? e.target.checked : e.checked;

        // Trata checkboxes
        if (type === 'checkbox') {
            value = checked;
        }

        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));

        // Limpa erro do campo quando o usuário começa a digitar
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    }, [errors]);

    /**
     * Valida o formulário
     * @returns {boolean} true se válido, false caso contrário
     */
    const validate = useCallback(() => {
        if (validator) {
            const validationErrors = validator(formData);
            if (validationErrors && Object.keys(validationErrors).length > 0) {
                setErrors(validationErrors);
                return false;
            }
        }
        setErrors({});
        return true;
    }, [formData, validator]);

    /**
     * Handler para submissão do formulário
     * @param {Event} e - Event do form (opcional)
     */
    const handleSubmit = useCallback(async (e) => {
        if (e && e.preventDefault) {
            e.preventDefault();
        }

        // Valida antes de submeter
        if (!validate()) {
            return;
        }

        setIsSubmitting(true);
        setErrors({});

        try {
            await onSubmit(formData);
        } catch (error) {
            const apiError = await parseApiError(error);
            const extracted = extractFieldErrors(apiError.details, {
                mapLocation: apiErrorOptions?.mapApiFieldLocation,
            });
            const formatted =
                apiErrorOptions?.formatApiErrors?.(apiError, extracted) ??
                defaultFormatApiErrors(apiError, extracted);
            setErrors(formatted);
        } finally {
            setIsSubmitting(false);
        }
    }, [formData, onSubmit, validate, apiErrorOptions]);

    /**
     * Reseta o formulário para os dados iniciais
     */
    const resetForm = useCallback(() => {
        setFormData(initialData);
        setErrors({});
        setIsSubmitting(false);
    }, [initialData]);

    /**
     * Atualiza um campo específico
     * @param {string} name - Nome do campo
     * @param {any} value - Valor do campo
     */
    const setField = useCallback((name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    }, []);

    return {
        formData,
        setFormData,
        errors,
        setErrors,
        isSubmitting,
        handleChange,
        handleSubmit,
        validate,
        resetForm,
        setField,
    };
}
